import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/AuthShell";
import { ErrorBox } from "@/components/Bits";
import ConfirmButton from "@/components/ConfirmButton";

function Transfer({ league, reload }) {
  const { user, loadLeagues } = useAuth();
  const [members, setMembers] = useState([]);
  const [target, setTarget] = useState("");
  const [password, setPassword] = useState("");
  useEffect(() => { api.get(`/leagues/${league.id}/members`).then(({ data }) => setMembers(data.filter((m) => m.id !== user.id && m.id !== league.owner_id))); }, [league.id, league.owner_id, user.id]);
  const submit = async () => {
    try {
      await api.post(`/leagues/${league.id}/transfer`, { new_owner_id: target, password });
      toast.success("Propriété transférée");
      setPassword("");
      setTarget("");
      await loadLeagues();
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const asAdmin = user.id !== league.owner_id;
  return (
    <section className="mpl-card space-y-3 p-4" data-testid="transfer-section">
      <h2 className="text-base font-bold">Transférer la propriété</h2>
      <p className="text-xs text-slate-400">Le nouveau propriétaire pourra gérer les membres et les invitations. Confirmez avec votre mot de passe.</p>
      <select className="mpl-input" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="transfer-member-select">
        <option value="">Choisir un membre…</option>
        {members.map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
      </select>
      <input className="mpl-input" type="password" placeholder="Votre mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="transfer-password-input" />
      {target && password && (
        <ConfirmButton label="Transférer" testId="transfer-button" title="Confirmer le transfert ?" confirmLabel="Transférer"
          description={asAdmin ? "L'actuel propriétaire redeviendra simple membre de la ligue." : "Vous deviendrez simple membre de la ligue. Cette action ne peut être annulée que par le nouveau propriétaire."} onConfirm={submit} />
      )}
    </section>
  );
}

function Featured({ league, reload }) {
  const [points, setPoints] = useState(league.featured?.points || league.scoring_rule.correct_points || 2);
  const save = async (enabled) => {
    try { await api.put(`/leagues/${league.id}/featured`, { enabled, points: Number(points) }); toast.success(enabled ? "Match vedette activé" : "Match vedette désactivé"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="mpl-card space-y-3 p-4" data-testid="featured-section">
      <h2 className="text-base font-bold">Match vedette du jour</h2>
      <p className="text-xs text-slate-400">Chaque jour, un match est mis en avant (les deux équipes au meilleur bilan). Un bon prono dessus rapporte des points en plus. S'applique uniquement aux matchs à venir, jamais aux résultats passés.</p>
      <p className="text-sm" data-testid="featured-state">{league.featured ? <>Activé : <b className="text-[#B7F52A]">+{league.featured.points} pts</b></> : "Désactivé"}</p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-xs">Bonus <input type="number" min={1} max={20} value={points} onChange={(e) => setPoints(e.target.value)} className="mpl-input w-20" data-testid="featured-points" /> pts</label>
        <button onClick={() => save(true)} className="mpl-btn-primary" data-testid="featured-enable">{league.featured ? "Mettre à jour" : "Activer"}</button>
        {league.featured && <button onClick={() => save(false)} className="mpl-btn-ghost" data-testid="featured-disable">Désactiver</button>}
      </div>
    </section>
  );
}

function SocialBonus({ league, reload }) {
  const save = async (enabled) => {
    try { await api.put(`/leagues/${league.id}/social-bonus`, { enabled }); toast.success(enabled ? "Bonus réseaux activé" : "Bonus réseaux désactivé"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="mpl-card space-y-3 p-4" data-testid="social-bonus-section">
      <h2 className="text-base font-bold">Bonus réseaux sociaux</h2>
      <p className="text-xs text-slate-400">+15 pts pour qui suit @mapetiteligue sur Instagram, +15 pts sur X, ajoutés au classement de la saison (pas à ceux de la semaine ou du mois). L'abonnement est déclaré par le joueur depuis l'app.</p>
      <div className="flex items-center gap-3">
        <span className="text-sm" data-testid="social-bonus-state">{league.social_bonus ? "Activé" : "Désactivé"}</span>
        <button onClick={() => save(!league.social_bonus)} className={league.social_bonus ? "mpl-btn-ghost" : "mpl-btn-primary"} data-testid="social-bonus-toggle">{league.social_bonus ? "Désactiver" : "Activer"}</button>
      </div>
    </section>
  );
}

export default function SettingsTab({ league, owner, reload, perms = {}, admin }) {
  const { loadLeagues } = useAuth();
  const corp = Boolean(league.brand) && !admin;
  const [form, setForm] = useState({ name: league.name, description: league.description || "", max_members: league.max_members });
  const [error, setError] = useState("");
  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.patch(`/leagues/${league.id}`, { description: form.description || null, ...(corp ? {} : { name: form.name, ...(league.unlimited ? {} : { max_members: Number(form.max_members) }) }) });
      toast.success("Ligue mise à jour");
      await loadLeagues();
      reload();
    } catch (err) { setError(errMsg(err)); }
  };
  if (!owner)
    return <p className="mpl-card p-4 text-sm text-slate-400" data-testid="settings-not-owner">Seul le propriétaire peut modifier les paramètres de la ligue.</p>;
  return (
    <div className="space-y-6" data-testid="settings-tab">
      {perms.info && <form onSubmit={save} className="mpl-card space-y-4 p-4" data-testid="league-settings-form">
        <Field label="Nom"><input className="mpl-input disabled:opacity-60" minLength={3} maxLength={50} value={form.name} disabled={corp} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="league-settings-name-input" /></Field>
        <Field label="Description"><textarea className="mpl-input min-h-[80px]" maxLength={280} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="league-settings-description-input" /></Field>
        {corp || league.unlimited ? corp && <p className="text-xs text-slate-400" data-testid="league-settings-corp-seats">{league.unlimited ? "Joueurs illimités" : `${league.max_members} places`} · Le nom et le nombre de places de cette ligue d'entreprise sont gérés par l'administration de Ma Petite Ligue.</p> : (
        <Field label={`Nombre de places (${Math.max(4, league.member_count)} à ${league.max_members_limit || 500}, modifiable à tout moment)`}>
          <input className="mpl-input w-32" type="number" min={Math.max(4, league.member_count)} max={Math.max(league.max_members_limit || 500, league.max_members)} value={form.max_members} onChange={(e) => setForm({ ...form, max_members: e.target.value })} data-testid="league-settings-max-members" />
        </Field>)}
        {!corp && league.unlimited && <p className="text-xs text-slate-400" data-testid="league-settings-unlimited">Joueurs illimités : réglé par l'administration de Ma Petite Ligue.</p>}
        {error && <ErrorBox message={error} />}
        <button className="mpl-btn-primary" data-testid="league-settings-save">Enregistrer</button>
      </form>}
      {perms.featured && <Featured league={league} reload={reload} />}
      {perms.social && <SocialBonus league={league} reload={reload} />}
      {perms.transfer && <Transfer league={league} reload={reload} />}
    </div>
  );
}
